import {useState, useRef, useCallback, useEffect} from 'react';
import type {FinancialYear, FinancialYearStatus, StagedDocument, DocumentType} from '../types/vault';
import {config} from '../data/config';
import {getRepository} from '../data/repository';
import {extractPastedImageFiles} from '../utils/clipboard';
import {RichTextEditor} from './RichTextEditor';
import {SmartDateField} from './SmartDateField';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiRefreshCw, FiUpload, FiFolder, FiX, FiCheck, FiAlertCircle, FiSkipForward} = FiIcons;

interface FinancialYearFormProps {
  year?: FinancialYear | null;
  profileId: string;
  onSave: (year: FinancialYear) => void | Promise<void>;
  onSaveWithDocuments?: (
    year: FinancialYear,
    staged: StagedDocument[]
  ) => Promise<{year: FinancialYear; results: StagedDocument[]}>;
  onCancel: () => void;
}

function numVal(v: number | null | undefined): string {
  return v != null ? String(v) : '';
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

function isEditableTarget(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if ((el as HTMLElement).isContentEditable) return true;
  if (el.closest('[contenteditable="true"]')) return true;
  return false;
}

function StagedDocumentItem({
  item,
  onUpdate,
  onRemove,
}: {
  item: StagedDocument;
  onUpdate: (id: string, updates: Partial<StagedDocument>) => void;
  onRemove: (id: string) => void;
}) {
  const isUploaded = item.status === 'uploaded';
  const isFailed = item.status === 'failed';
  const isUploading = item.status === 'uploading';
  const isDisabled = isUploaded || isUploading;

  const statusConfig: Record<string, {label: string; className: string; icon?: any}> = {
    staged: {label: 'Staged', className: 'bg-amber-100 text-amber-700'},
    ready: {label: 'Ready', className: 'bg-teal-100 text-teal-700'},
    uploading: {label: 'Uploading…', className: 'bg-blue-100 text-blue-700', icon: FiRefreshCw},
    uploaded: {label: 'Uploaded', className: 'bg-green-100 text-green-700', icon: FiCheck},
    failed: {label: 'Failed', className: 'bg-red-100 text-red-700', icon: FiAlertCircle},
    skipped: {label: 'Skipped', className: 'bg-slate-100 text-slate-600', icon: FiSkipForward},
  };

  const sc = statusConfig[item.status] || statusConfig.staged;

  return (
    <div className={`rounded-lg border p-3 space-y-2 transition-colors ${isFailed ? 'border-red-200 bg-red-50/30' : isUploaded ? 'border-green-200 bg-green-50/20' : 'border-slate-200 bg-slate-50/50'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-900 truncate">{item.file.name}</p>
          <p className="text-xs text-slate-500">{formatFileSize(item.file.size)}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${sc.className}`}>
            {sc.icon && <SafeIcon icon={sc.icon} className={`w-3 h-3 mr-1 ${isUploading ? 'animate-spin' : ''}`} />}
            {sc.label}
          </span>
          {!isDisabled && (
            <button
              type="button"
              onClick={() => onRemove(item.id)}
              className="p-1 text-slate-400 hover:text-red-600 rounded focus:outline-none focus:ring-2 focus:ring-red-300"
              aria-label={`Remove ${item.file.name}`}
            >
              <SafeIcon icon={FiX} className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      {!isUploaded && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-0.5">Title *</label>
            <input
              type="text"
              value={item.title}
              onChange={e => onUpdate(item.id, {title: e.target.value})}
              maxLength={config.limits.documentTitle}
              className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0F766E]"
              placeholder="Document title"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-0.5">Document Type</label>
            <select
              value={item.documentType}
              onChange={e => onUpdate(item.id, {documentType: e.target.value as DocumentType})}
              className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0F766E] bg-white"
            >
              {config.documentCategories.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        </div>
      )}
      {isFailed && item.error && (
        <p className="text-xs text-red-600 leading-relaxed">{item.error}</p>
      )}
    </div>
  );
}

export function FinancialYearForm({year, profileId, onSave, onSaveWithDocuments, onCancel}: FinancialYearFormProps) {
  const [endingYear, setEndingYear] = useState(year?.endingYear ?? new Date().getFullYear());
  const [status, setStatus] = useState<FinancialYearStatus>(year?.status ?? 'In Progress');
  const [grossIncome, setGrossIncome] = useState(numVal(year?.grossIncome));
  const [taxableIncome, setTaxableIncome] = useState(numVal(year?.taxableIncome));
  const [taxWithheld, setTaxWithheld] = useState(numVal(year?.taxWithheld));
  const [taxRefund, setTaxRefund] = useState(numVal(year?.taxRefund));
  const [taxPayable, setTaxPayable] = useState(numVal(year?.taxPayable));
  const [capitalGainLoss, setCapitalGainLoss] = useState(numVal(year?.capitalGainLoss));
  const [carryForwardLoss, setCarryForwardLoss] = useState(numVal(year?.carryForwardLoss));
  const [superContributions, setSuperContributions] = useState(numVal(year?.superContributions));
  const [accountantFee, setAccountantFee] = useState(numVal(year?.accountantFee));
  const [lodgementDate, setLodgementDate] = useState(year?.lodgementDate ?? '');
  const [assessmentDate, setAssessmentDate] = useState(year?.assessmentDate ?? '');
  const [annualSummary, setAnnualSummary] = useState(year?.annualSummary ?? '');
  const [validationError, setValidationError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Inline document staging state
  const [stagedDocs, setStagedDocs] = useState<StagedDocument[]>([]);
  const [rejectedFiles, setRejectedFiles] = useState<{name: string; reason: string}[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [pasteConfirm, setPasteConfirm] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragDepthRef = useRef(0);
  const savedYearRef = useRef<FinancialYear | null>(null);
  const pasteTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const isNewYear = !year;
  const showDocSection = isNewYear && !!onSaveWithDocuments;

  const toNum = (v: string, fieldName: string): number | null => {
    if (v.trim() === '') return null;
    const n = Number(v);
    if (!Number.isFinite(n)) throw new Error(`Invalid number in ${fieldName}`);
    return n;
  };

  const handleRefundChange = (val: string) => {
    setTaxRefund(val);
    const n = Number(val);
    if (Number.isFinite(n) && n > 0) { setTaxPayable(''); setValidationError(''); }
  };

  const handlePayableChange = (val: string) => {
    setTaxPayable(val);
    const n = Number(val);
    if (Number.isFinite(n) && n > 0) { setTaxRefund(''); setValidationError(''); }
  };

  const constructPayload = (): FinancialYear | null => {
    try {
      const refund = toNum(taxRefund, 'Tax Refund');
      const payable = toNum(taxPayable, 'Amount Payable');
      if ((refund ?? 0) > 0 && (payable ?? 0) > 0) {
        setValidationError('Enter either a Tax Refund or an Amount Payable, not both. Amount Payable means the final balance owing, not total assessed tax.');
        return null;
      }
      if ((refund ?? 0) < 0 || (payable ?? 0) < 0) {
        setValidationError('Refund and Amount Payable cannot be negative.');
        return null;
      }
      setValidationError('');
      const now = new Date().toISOString();
      return {
        id: year?.id ?? '',
        profileId,
        endingYear,
        status,
        grossIncome: toNum(grossIncome, 'Gross Income'),
        taxableIncome: toNum(taxableIncome, 'Taxable Income'),
        taxWithheld: toNum(taxWithheld, 'Tax Withheld'),
        taxRefund: refund,
        taxPayable: payable,
        capitalGainLoss: toNum(capitalGainLoss, 'Capital Gain/Loss'),
        carryForwardLoss: toNum(carryForwardLoss, 'Carry Forward Loss'),
        superContributions: toNum(superContributions, 'Super Contributions'),
        accountantFee: toNum(accountantFee, 'Accountant Fee'),
        lodgementDate: lodgementDate || undefined,
        assessmentDate: assessmentDate || undefined,
        annualSummary: annualSummary || undefined,
        archived: year?.archived ?? false,
        created: year?.created ?? now,
        updated: now,
      };
    } catch (err) {
      setValidationError(err instanceof Error ? err.message : 'Validation failed.');
      return null;
    }
  };

  // --- File staging handlers ---
  const addFiles = useCallback(async (files: File[]) => {
    const newRejected: {name: string; reason: string}[] = [];
    const validFiles: File[] = [];
    for (const file of files) {
      if (file.size === 0) {
        newRejected.push({name: file.name, reason: 'This file is empty and cannot be uploaded.'});
        continue;
      }
      if (file.size > config.upload.maxFileSize) {
        newRejected.push({name: file.name, reason: `File exceeds the ${config.upload.maxFileSizeLabel} limit.`});
        continue;
      }
      const ext = '.' + (file.name.split('.').pop()?.toLowerCase() || '');
      if (!config.upload.allowedExtensions.includes(ext)) {
        newRejected.push({name: file.name, reason: 'Unsupported file type. Upload PDF, image, CSV, Excel or Word files.'});
        continue;
      }
      const isDup = stagedDocs.some(
        q => q.file.name === file.name && q.file.size === file.size && q.file.lastModified === file.lastModified
      );
      if (!isDup) validFiles.push(file);
    }
    setRejectedFiles(prev => [...prev, ...newRejected]);
    if (validFiles.length > 0) {
      const staged = await getRepository().stageDocuments(validFiles);
      const newItems: StagedDocument[] = staged.map(item => ({
        ...item,
        title: item.file.name.replace(/\.[^/.]+$/, ''),
        profileId,
        documentType: 'Tax Return' as DocumentType,
        final: false,
        sensitive: false,
        status: 'staged' as const,
      }));
      setStagedDocs(prev => [...prev, ...newItems]);
    }
  }, [profileId, stagedDocs]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) { addFiles(Array.from(e.target.files)); e.target.value = ''; }
  };

  const handleDragEnter = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); dragDepthRef.current += 1; setIsDragging(true); };
  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); dragDepthRef.current -= 1; if (dragDepthRef.current === 0) setIsDragging(false); };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); dragDepthRef.current = 0; setIsDragging(false);
    if (e.dataTransfer.files?.length) { addFiles(Array.from(e.dataTransfer.files)); }
  };

  const updateStagedDoc = (id: string, updates: Partial<StagedDocument>) => {
    setStagedDocs(prev => prev.map(item => (item.id === id ? {...item, ...updates} : item)));
  };

  const removeStagedDoc = (id: string) => {
    setStagedDocs(prev => prev.filter(item => item.id !== id));
  };

  // --- Paste handler for Supporting Documents ---
  useEffect(() => {
    if (!showDocSection) return;

    const handlePaste = (e: ClipboardEvent) => {
      if (isEditableTarget(e.target as Element)) return;
      const imageFiles = extractPastedImageFiles(e.clipboardData?.items ?? null);
      if (imageFiles.length === 0) return;
      e.preventDefault();
      addFiles(imageFiles);
      setPasteConfirm(`${imageFiles.length} pasted screenshot${imageFiles.length !== 1 ? 's' : ''} added`);
      clearTimeout(pasteTimerRef.current);
      pasteTimerRef.current = setTimeout(() => setPasteConfirm(null), 3000);
    };

    document.addEventListener('paste', handlePaste);
    return () => {
      document.removeEventListener('paste', handlePaste);
      clearTimeout(pasteTimerRef.current);
    };
  }, [showDocSection, addFiles]);

  // --- Submission ---
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = constructPayload();
    if (!payload) return;

    const pendingDocs = stagedDocs.filter(
      d => d.status !== 'uploaded' && d.status !== 'skipped'
    );

    // Validate staged document titles
    for (const doc of pendingDocs) {
      if (!doc.title.trim()) {
        setValidationError('All staged documents must have a title.');
        return;
      }
    }

    setIsSubmitting(true);
    setSaveError('');

    try {
      if (pendingDocs.length > 0 && onSaveWithDocuments) {
        const yearToSend = savedYearRef.current
          ? {...payload, id: savedYearRef.current.id}
          : payload;

        const result = await onSaveWithDocuments(yearToSend, pendingDocs);
        savedYearRef.current = result.year;

        setStagedDocs(prev => prev.map(item => {
          const r = result.results.find(res => res.id === item.id);
          return r || item;
        }));

        const hasFailures = result.results.some(r => r.status === 'failed');
        if (!hasFailures) {
          onCancel();
        }
      } else if (savedYearRef.current && pendingDocs.length === 0) {
        onCancel();
      } else {
        await onSave(payload);
      }
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayError = validationError || saveError;

  const pendingCount = stagedDocs.filter(d => d.status !== 'uploaded' && d.status !== 'skipped').length;
  const uploadedCount = stagedDocs.filter(d => d.status === 'uploaded').length;
  const failedCount = stagedDocs.filter(d => d.status === 'failed').length;

  const getButtonText = (): string => {
    if (year) return 'Save Changes';
    if (pendingCount === 0 && savedYearRef.current) return 'Done';
    if (pendingCount === 0) return 'Save Financial Year';
    if (pendingCount === 1) return 'Save Financial Year & Upload 1 Document';
    return `Save Financial Year & Upload ${pendingCount} Documents`;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {displayError && (
        <div id="financial-year-validation" role="alert" className="p-3 text-sm rounded-lg border bg-red-50 text-red-700 border-red-100">
          {displayError}
        </div>
      )}

      {/* Ending Year + Status */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Ending Year <span className="text-red-500">*</span>
          </label>
          <input type="number" value={endingYear} onChange={e => setEndingYear(Number(e.target.value))} required className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0F766E]" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Status <span className="text-red-500">*</span>
          </label>
          <select value={status} onChange={e => setStatus(e.target.value as FinancialYearStatus)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0F766E]">
            {config.financialYearStatuses.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      {/* ROW 1: Income fields */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Gross Income</label>
          <input type="number" step="0.01" value={grossIncome} onChange={e => setGrossIncome(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0F766E]" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Taxable Income</label>
          <input type="number" step="0.01" value={taxableIncome} onChange={e => setTaxableIncome(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0F766E]" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Tax Withheld</label>
          <input type="number" step="0.01" value={taxWithheld} onChange={e => setTaxWithheld(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0F766E]" />
        </div>
      </div>

      {/* ROW 2: Tax outcome */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <div className="sm:h-10 sm:flex sm:items-end mb-1">
            <label className="text-sm font-medium text-slate-700">Tax Refund</label>
          </div>
          <input type="number" step="0.01" min="0" value={taxRefund} onChange={e => handleRefundChange(e.target.value)} aria-describedby="tax-outcome-helper" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0F766E]" />
        </div>
        <div>
          <div className="sm:h-10 sm:flex sm:items-end mb-1">
            <label className="text-sm font-medium text-slate-700">Amount Payable / Balance Owing</label>
          </div>
          <input type="number" step="0.01" min="0" value={taxPayable} onChange={e => handlePayableChange(e.target.value)} aria-describedby="tax-outcome-helper" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0F766E]" />
        </div>
      </div>

      <p id="tax-outcome-helper" className="text-xs text-slate-500 -mt-2">
        Enter one outcome only. A tax return can result in a refund, an amount owing, or neither. Do not enter the total assessed tax in Amount Payable.
      </p>

      {/* ROW 3: Secondary financial fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Capital Gain/Loss</label>
          <input type="number" step="0.01" value={capitalGainLoss} onChange={e => setCapitalGainLoss(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0F766E]" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Carry Fwd Loss</label>
          <input type="number" step="0.01" value={carryForwardLoss} onChange={e => setCarryForwardLoss(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0F766E]" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Super Contribs</label>
          <input type="number" step="0.01" value={superContributions} onChange={e => setSuperContributions(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0F766E]" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Accountant Fee</label>
          <input type="number" step="0.01" value={accountantFee} onChange={e => setAccountantFee(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0F766E]" />
        </div>
      </div>

      {/* Dates */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SmartDateField label="Lodgement Date" id="lodgementDate" value={lodgementDate} onChange={setLodgementDate} />
        <SmartDateField label="Assessment Date" id="assessmentDate" value={assessmentDate} onChange={setAssessmentDate} />
      </div>

      {/* Annual Summary */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Annual Summary</label>
        <RichTextEditor value={annualSummary} onChange={setAnnualSummary} placeholder="Enter a summary of the financial year..." />
      </div>

      {/* Supporting Documents — new years only */}
      {showDocSection && (
        <div className="space-y-3 pt-2 border-t border-slate-200">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Supporting Documents</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Optional. Drop files, browse, or paste a screenshot with <kbd className="px-1 py-0.5 bg-slate-100 border border-slate-200 rounded text-[10px] font-sans">Ctrl+V</kbd> / <kbd className="px-1 py-0.5 bg-slate-100 border border-slate-200 rounded text-[10px] font-sans">⌘V</kbd>. They will be linked automatically to this profile and financial year when saved.
            </p>
          </div>

          {pasteConfirm && (
            <div className="rounded-lg p-2.5 border bg-teal-50 border-teal-200 flex items-center">
              <SafeIcon icon={FiCheck} className="w-4 h-4 text-teal-600 mr-2 shrink-0" />
              <p className="text-xs text-teal-800">{pasteConfirm}</p>
            </div>
          )}

          {/* Rejected files */}
          {rejectedFiles.length > 0 && (
            <div className="rounded-lg p-2.5 border bg-red-50 border-red-200 space-y-1">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-medium text-red-800">Rejected Files</h4>
                <button type="button" onClick={() => setRejectedFiles([])} className="text-xs text-red-600 hover:text-red-800 font-medium">Clear</button>
              </div>
              {rejectedFiles.map((r, i) => (
                <p key={i} className="text-xs text-red-700"><strong>{r.name}</strong>: {r.reason}</p>
              ))}
            </div>
          )}

          {/* Drop zone */}
          <div
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            tabIndex={0}
            role="region"
            aria-label="Supporting documents drop zone. Drag and drop files, browse, or paste a screenshot."
            className={`border-2 border-dashed rounded-xl p-5 text-center transition-colors focus:outline-none focus:ring-2 focus:ring-[#0F766E] ${isDragging ? 'border-[#0F766E] bg-[#0F766E]/5' : 'border-slate-300'}`}
          >
            {isDragging ? (
              <div className="space-y-1">
                <SafeIcon icon={FiUpload} className="w-8 h-8 mx-auto text-[#0F766E]" />
                <p className="text-sm font-medium text-[#0F766E]">Drop files to add them</p>
              </div>
            ) : (
              <div className="space-y-2">
                <SafeIcon icon={FiUpload} className="w-8 h-8 mx-auto text-slate-400" />
                <p className="text-sm text-slate-500">
                  Drag and drop files here, browse, or paste a screenshot with <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-xs font-sans">Ctrl+V</kbd> / <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-xs font-sans">⌘V</kbd>
                </p>
                <p className="text-xs text-slate-400">
                  Max {config.upload.maxFileSizeLabel} per file • PDF, images, CSV, Excel, Word
                </p>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center px-4 py-2 bg-[#102D4C] text-white text-sm font-medium rounded-lg hover:bg-[#173B61] focus:outline-none focus:ring-2 focus:ring-[#0F766E]"
                >
                  <SafeIcon icon={FiFolder} className="w-4 h-4 mr-2" /> Browse Files
                </button>
              </div>
            )}
            <input ref={fileInputRef} type="file" accept={config.upload.acceptAttribute} multiple onChange={handleFileSelect} className="hidden" />
          </div>

          {/* Staged documents list */}
          {stagedDocs.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-medium text-slate-700">
                  Staged Files ({stagedDocs.length})
                </h4>
                {uploadedCount > 0 && (
                  <span className="text-xs text-green-600 font-medium">{uploadedCount} uploaded</span>
                )}
              </div>
              {stagedDocs.map(item => (
                <StagedDocumentItem key={item.id} item={item} onUpdate={updateStagedDoc} onRemove={removeStagedDoc} />
              ))}
              {failedCount > 0 && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                  {failedCount} file{failedCount !== 1 ? 's' : ''} failed. Remove or retry below.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Footer actions */}
      <div className="flex flex-wrap justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} disabled={isSubmitting} className="px-4 py-2 min-h-[44px] text-sm text-slate-600 hover:bg-slate-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F766E] disabled:opacity-50">
          Cancel
        </button>
        <button type="submit" disabled={isSubmitting} className="px-4 py-2 min-h-[44px] text-sm bg-[#102D4C] text-white rounded-lg hover:bg-[#173B61] focus:outline-none focus:ring-2 focus:ring-[#0F766E] disabled:opacity-50 flex items-center">
          {isSubmitting ? (
            <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2 animate-spin" />
          ) : null}
          {getButtonText()}
        </button>
      </div>
    </form>
  );
}